/*
  Feuilleter un extrait (snippets/sesames-feuilleter.liquid) : visionneuse plein écran, sans application.
  - Vues : sur un écran large, doubles pages (livre) ou planche et texte lu au dos (kamishibaï) ; sur un écran
    étroit, une page à la fois. La première image (couverture, planche de titre) s'affiche seule, au centre.
    Avec les numéros de page (sesames.feuilleter_pages), les versos blancs s'affichent sans image à charger.
  - Livre : la page tourne autour du dos (CSS 3D) ; kamishibaï et écran étroit : la vue glisse, comme une planche
    tirée du butaï.
  - Flèches, clavier (← →, Début, Fin, Échap), glisser du doigt, loupe sur chaque page, puis écran de fin
    « Choisir mon format ». Mouvement réduit : pages changées sans animation.
  - Évènement « sesames_feuilleter » (ouverture, fin, commander) publié pour un pixel personnalisé.
*/
(() => {
  const ETROIT = window.matchMedia('(max-width: 749px), (max-aspect-ratio: 4/5)');
  const SANS_MOUVEMENT = window.matchMedia('(prefers-reduced-motion: reduce)');
  const attendre = (ms) => new Promise((resoudre) => setTimeout(resoudre, ms));

  function preparer(dialog) {
    if (dialog.sFeuilleter) return;
    let D;
    try {
      D = JSON.parse(dialog.querySelector('[data-s-feuilleter-donnees]').textContent);
    } catch (erreur) {
      return;
    }
    const pages = (D && D.pages) || [];
    if (!pages.length) return;
    const kami = D.mode === 'kamishibai';
    const q = (selecteur) => dialog.querySelector(selecteur);
    const el = {
      livre: q('[data-livre]'),
      gauche: q('[data-page="gauche"]'),
      droite: q('[data-page="droite"]'),
      scene: q('[data-scene]'),
      compteur: q('[data-compteur]'),
      prec: q('[data-prec]'),
      suiv: q('[data-suiv]'),
      fin: q('[data-fin]'),
      legende: q('[data-legende]'),
      zoom: q('[data-zoom]'),
      zoomImage: q('[data-zoom-image]'),
    };
    const ratio = (pages[0].l || 1) / (pages[0].h || 1);
    /* Numéros de page des images après la couverture (métachamp facultatif sesames.feuilleter_pages, « 1, 3, 7, 8 ») :
       une page de droite dont la page de gauche n'est pas dans l'extrait (verso blanc) s'affiche à côté d'une page
       blanche ; sur un écran étroit, les pages blanches n'existent pas. */
    const BLANCHE = -1;
    const numeros = String(D.numeros || '').split(/[\s,;]+/).filter(Boolean).map(Number);
    const avecNumeros = !kami && numeros.length === pages.length - 1 && numeros.every((n) => Number.isInteger(n) && n > 0);
    const reelle = (i) => i !== null && i !== undefined && i >= 0;
    let vues = [];
    let index = 0;
    let simple = ETROIT.matches;
    let largeurPage = 600;
    let enCours = false;
    let finAffichee = false;
    let ouvreur = null;
    let glisseRecente = false;

    /* ---------- Vues ---------- */

    const construireVues = () => {
      simple = ETROIT.matches;
      if (simple) return pages.map((_, i) => ({ g: null, d: i, seule: true }));
      const v = [{ g: null, d: 0, seule: true }];
      if (avecNumeros) {
        let gauche = null; /* vue dont la page de gauche attend sa page de droite */
        for (let i = 1; i < pages.length; i += 1) {
          const n = numeros[i - 1];
          if (n % 2 === 0) {
            gauche = { g: i, d: null };
            v.push(gauche);
          } else {
            if (gauche && numeros[gauche.g - 1] === n - 1) gauche.d = i;
            else v.push({ g: i === 1 ? null : BLANCHE, d: i });
            gauche = null;
          }
        }
        return v;
      }
      let i = 1;
      if (!kami && pages.length > 1) {
        v.push({ g: null, d: 1 });
        i = 2;
      }
      for (; i < pages.length; i += 2) v.push({ g: i, d: i + 1 < pages.length ? i + 1 : null });
      return v;
    };

    const mettreEnPage = () => {
      const r = el.scene.getBoundingClientRect();
      const colonnes = simple ? 1 : 2;
      const ecart = kami && !simple ? 24 : 0; /* les planches sont séparées, les pages d'un livre se touchent */
      /* Sur écran large, les flèches gardent leur place de chaque côté ; sur mobile, elles sont en bas */
      const largeurDispo = r.width - (simple ? 24 : 2 * (56 + 20 + 16)) - ecart;
      const hauteurDispo = r.height - (simple ? 24 : 40) - (el.legende && !simple ? 34 : 0);
      largeurPage = Math.max(120, Math.floor(Math.min(largeurDispo / colonnes, hauteurDispo * ratio)));
      /* Variables posées sur la scène : le livre, les feuillets et la légende s'en servent */
      el.scene.style.setProperty('--page-l', `${largeurPage}px`);
      el.scene.style.setProperty('--page-h', `${Math.floor(largeurPage / ratio)}px`);
      el.scene.style.setProperty('--ecart', `${ecart}px`);
      el.livre.classList.toggle('est-simple', simple);
    };

    const image = (i) => {
      const p = pages[i];
      const img = document.createElement('img');
      img.sizes = `${largeurPage}px`;
      img.srcset = p.srcset;
      img.src = p.src;
      img.alt = '';
      img.width = p.l;
      img.height = p.h;
      img.decoding = 'async';
      img.draggable = false;
      return img;
    };

    const remplir = (emplacement, i) => {
      emplacement.textContent = '';
      const vide = i === null || i === undefined;
      emplacement.classList.toggle('est-vide', vide);
      if (vide || i === BLANCHE) return; /* page blanche : le fond blanc de l'emplacement suffit */
      const bouton = document.createElement('button');
      bouton.type = 'button';
      bouton.className = 's-feuilleter__agrandir';
      bouton.dataset.zoomIndex = String(i);
      bouton.setAttribute('aria-label', `Agrandir : ${pages[i].alt || `page ${i + 1}`}`);
      bouton.append(image(i));
      emplacement.append(bouton);
    };

    const majEtat = () => {
      const v = vues[index] || {};
      const visible = el.compteur.querySelector('[aria-hidden]') || document.createElement('span');
      const lu = el.compteur.querySelector('.visually-hidden') || document.createElement('span');
      visible.setAttribute('aria-hidden', 'true');
      lu.className = 'visually-hidden';
      if (finAffichee) {
        visible.textContent = 'Fin de l’extrait';
        lu.textContent = 'Fin de l’extrait.';
      } else {
        visible.textContent = `${index + 1} / ${vues.length}`;
        const textes = [v.g, v.d].filter(reelle).map((i) => pages[i].alt).filter(Boolean);
        lu.textContent = `Vue ${index + 1} sur ${vues.length}. ${textes.join('. ')}`;
      }
      if (!visible.parentNode) el.compteur.append(visible, lu);
      el.prec.disabled = !finAffichee && index === 0;
      el.suiv.disabled = finAffichee;
      el.prec.setAttribute('aria-label', simple ? 'Page précédente' : 'Pages précédentes');
      el.suiv.setAttribute('aria-label', index === vues.length - 1 ? 'Fin de l’extrait' : (simple ? 'Page suivante' : 'Pages suivantes'));
      if (el.legende) el.legende.hidden = simple || finAffichee || !!v.seule;
    };

    const afficher = (i) => {
      index = Math.max(0, Math.min(i, vues.length - 1));
      const v = vues[index];
      remplir(el.gauche, simple ? null : v.g);
      remplir(el.droite, v.d);
      el.livre.classList.toggle('est-fermee', !simple && !!v.seule);
      majEtat();
    };

    const precharger = () => {
      [vues[index + 1], vues[index - 1]].forEach((v) => {
        if (!v) return;
        [v.g, v.d].forEach((i) => {
          if (!reelle(i)) return;
          const im = new Image();
          im.sizes = `${largeurPage}px`;
          im.srcset = pages[i].srcset;
          im.src = pages[i].src;
        });
      });
    };

    /* ---------- Animations ---------- */

    const feuillet = (cote, recto, verso) => {
      const f = document.createElement('div');
      f.className = `s-feuilleter__feuillet s-feuilleter__feuillet--${cote}`;
      const faceA = document.createElement('div');
      faceA.className = 's-feuilleter__face s-feuilleter__face--recto';
      const faceB = document.createElement('div');
      faceB.className = 's-feuilleter__face s-feuilleter__face--verso';
      if (reelle(recto)) faceA.append(image(recto));
      if (reelle(verso)) faceB.append(image(verso));
      f.append(faceA, faceB);
      el.livre.append(f);
      return f;
    };

    /* La fin de l'animation est garantie par une minuterie, même si l'onglet ne rafraîchit plus l'affichage */
    const jouer = (f) => new Promise((resoudre) => {
      let fini = false;
      const terminer = () => {
        if (fini) return;
        fini = true;
        resoudre();
      };
      setTimeout(terminer, 1200);
      f.addEventListener('transitionend', terminer, { once: true });
      void f.offsetWidth; /* position de départ calculée avant la rotation */
      f.classList.add('est-tourne');
    });

    const tourner = async (sens, cible) => {
      const v = vues[index];
      const w = vues[cible];
      if (sens > 0) {
        const f = feuillet('droite', v.d, w.g);
        remplir(el.droite, w.d);
        if (v.seule) el.livre.classList.remove('est-fermee');
        await jouer(f);
        remplir(el.gauche, w.g);
        f.remove();
      } else {
        const f = feuillet('gauche', v.g, w.d);
        remplir(el.gauche, w.g);
        if (w.seule) el.livre.classList.add('est-fermee');
        await jouer(f);
        remplir(el.droite, w.d);
        f.remove();
      }
      index = cible;
      majEtat();
    };

    const glisser = async (sens, cible) => {
      el.livre.classList.add(sens > 0 ? 'sort-gauche' : 'sort-droite');
      await attendre(200);
      el.livre.classList.remove('sort-gauche', 'sort-droite');
      el.livre.classList.add(sens > 0 ? 'entre-droite' : 'entre-gauche');
      afficher(cible);
      void el.livre.offsetWidth;
      el.livre.classList.remove('entre-droite', 'entre-gauche');
      await attendre(260);
    };

    /* ---------- Navigation ---------- */

    const montrerFin = () => {
      finAffichee = true;
      el.fin.hidden = false;
      majEtat();
      const bouton = el.fin.querySelector('[data-commander]');
      if (bouton) bouton.focus({ preventScroll: true });
      publier('fin');
    };

    const masquerFin = () => {
      finAffichee = false;
      el.fin.hidden = true;
      majEtat();
      el.suiv.focus({ preventScroll: true });
    };

    const aller = async (sens) => {
      if (enCours) return;
      if (finAffichee) {
        if (sens < 0) masquerFin();
        return;
      }
      const cible = index + sens;
      if (cible >= vues.length) {
        montrerFin();
        return;
      }
      if (cible < 0) return;
      enCours = true;
      try {
        if (SANS_MOUVEMENT.matches) afficher(cible);
        else if (!simple && !kami) await tourner(sens, cible);
        else await glisser(sens, cible);
      } finally {
        enCours = false;
        majEtat();
        precharger();
      }
    };

    const allerA = (cible) => {
      if (enCours) return;
      if (finAffichee) {
        finAffichee = false;
        el.fin.hidden = true;
      }
      afficher(cible);
      precharger();
    };

    /* ---------- Loupe ---------- */

    const ouvrirZoom = (i) => {
      el.zoomImage.src = pages[i].zoom;
      el.zoomImage.alt = pages[i].alt || '';
      el.zoom.hidden = false;
      el.zoom.scrollTop = 0;
      el.zoom.scrollLeft = 0;
      el.zoom.querySelector('[data-zoom-fermer]').focus({ preventScroll: true });
    };

    const fermerZoom = (rendreFocus) => {
      if (el.zoom.hidden) return;
      el.zoom.hidden = true;
      el.zoomImage.removeAttribute('src');
      if (rendreFocus) el.suiv.focus({ preventScroll: true });
    };

    /* ---------- Ouverture et fermeture ---------- */

    const publier = (action) => {
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('sesames_feuilleter', { action, produit: D.titre, vue: index + 1, vues: vues.length });
        }
      } catch (erreur) { /* mesure facultative */ }
    };

    const ouvrir = (declencheur) => {
      if (dialog.open) return;
      ouvreur = declencheur || document.activeElement;
      finAffichee = false;
      el.fin.hidden = true;
      fermerZoom(false);
      vues = construireVues();
      document.documentElement.classList.add('s-feuilleter-ouvert');
      dialog.showModal();
      mettreEnPage();
      afficher(0);
      precharger();
      el.suiv.focus({ preventScroll: true });
      publier('ouverture');
    };

    /* Remise en état de la page : faite dès la fermeture, et de nouveau (sans effet) à l'évènement « close »,
       que certains navigateurs envoient en différé (fermeture par Échap comprise) */
    const nettoyer = () => {
      document.documentElement.classList.remove('s-feuilleter-ouvert');
      fermerZoom(false);
      if (ouvreur && typeof ouvreur.focus === 'function') ouvreur.focus({ preventScroll: true });
      ouvreur = null;
    };

    const fermer = () => {
      if (dialog.open) dialog.close();
      nettoyer();
    };

    dialog.addEventListener('close', nettoyer);

    dialog.addEventListener('cancel', (evenement) => {
      if (!el.zoom.hidden) {
        evenement.preventDefault();
        fermerZoom(true);
      }
    });

    dialog.addEventListener('keydown', (evenement) => {
      if (!el.zoom.hidden || evenement.altKey || evenement.ctrlKey || evenement.metaKey) return;
      const touches = { ArrowRight: 1, PageDown: 1, ArrowLeft: -1, PageUp: -1 };
      if (touches[evenement.key]) {
        evenement.preventDefault();
        aller(touches[evenement.key]);
      } else if (evenement.key === 'Home') {
        evenement.preventDefault();
        allerA(0);
      } else if (evenement.key === 'End') {
        evenement.preventDefault();
        allerA(vues.length - 1);
      }
    });

    q('[data-fermer]').addEventListener('click', fermer);
    el.prec.addEventListener('click', () => aller(-1));
    el.suiv.addEventListener('click', () => aller(1));
    q('[data-zoom-fermer]').addEventListener('click', () => fermerZoom(true));
    el.zoomImage.addEventListener('click', () => fermerZoom(true));
    q('[data-recommencer]').addEventListener('click', () => {
      allerA(0);
      el.suiv.focus({ preventScroll: true });
    });
    q('[data-commander]').addEventListener('click', (evenement) => {
      const ancre = document.getElementById(evenement.currentTarget.dataset.ancre);
      publier('commander');
      ouvreur = null;
      fermer();
      if (!ancre) return;
      ancre.scrollIntoView({ behavior: SANS_MOUVEMENT.matches ? 'auto' : 'smooth', block: 'center' });
      const zone = ancre.closest('.product__info-wrapper') || document;
      const champ = zone.querySelector('input[type="radio"]:checked, input[type="radio"], .product-form__submit');
      if (champ) setTimeout(() => champ.focus({ preventScroll: true }), SANS_MOUVEMENT.matches ? 0 : 450);
    });

    el.livre.addEventListener('click', (evenement) => {
      const bouton = evenement.target.closest('[data-zoom-index]');
      if (!bouton || glisseRecente || enCours) return;
      ouvrirZoom(parseInt(bouton.dataset.zoomIndex, 10));
    });

    /* Glisser du doigt : gauche = suivant, droite = précédent (le défilement vertical et le zoom restent natifs) */
    let depart = null;
    el.scene.addEventListener('pointerdown', (evenement) => {
      if (evenement.pointerType === 'mouse' || !evenement.isPrimary) return;
      depart = { x: evenement.clientX, y: evenement.clientY };
    });
    el.scene.addEventListener('pointerup', (evenement) => {
      if (!depart) return;
      const dx = evenement.clientX - depart.x;
      const dy = evenement.clientY - depart.y;
      depart = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        glisseRecente = true;
        setTimeout(() => { glisseRecente = false; }, 350);
        aller(dx < 0 ? 1 : -1);
      }
    });
    el.scene.addEventListener('pointercancel', () => { depart = null; });

    /* Changement de taille : même page affichée, mise en page recalculée */
    let minuterie = null;
    const recalculer = () => {
      if (!dialog.open || enCours) return;
      const v = vues[index] || {};
      const page = reelle(v.d) ? v.d : (reelle(v.g) ? v.g : 0);
      vues = construireVues();
      const nouvelle = Math.max(0, vues.findIndex((x) => x.g === page || x.d === page));
      mettreEnPage();
      afficher(nouvelle);
    };
    window.addEventListener('resize', () => {
      clearTimeout(minuterie);
      minuterie = setTimeout(recalculer, 150);
    });

    dialog.sFeuilleter = { ouvrir };
  }

  const initialiser = () => {
    document.querySelectorAll('dialog[data-s-feuilleter]').forEach(preparer);
    if (window.location.hash === '#feuilleter') {
      const dialog = document.querySelector('dialog[data-s-feuilleter]');
      if (dialog && dialog.sFeuilleter) dialog.sFeuilleter.ouvrir(null);
    }
  };

  document.addEventListener('click', (evenement) => {
    const bouton = evenement.target.closest && evenement.target.closest('[data-s-feuilleter-ouvrir]');
    if (!bouton) return;
    const section = bouton.closest('.s-produit, section') || document;
    const dialog = section.querySelector('dialog[data-s-feuilleter]') || document.querySelector('dialog[data-s-feuilleter]');
    if (!dialog) return;
    preparer(dialog);
    if (dialog.sFeuilleter) {
      evenement.preventDefault();
      dialog.sFeuilleter.ouvrir(bouton);
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialiser);
  else initialiser();
  document.addEventListener('shopify:section:load', initialiser);
})();
